import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {
  TablePagination,
  IconButton,
  Tooltip,
  Icon,
  Button,
  useTheme,
} from "@material-ui/core";
import PlayIcon from "@material-ui/icons/PlayCircleFilled";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ColumnWidth } from "../../../blocks/ColumnHelper";
import { LightTooltip } from "../../../controls";
import { getSeason } from "../../../functions/GetSeason";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";

import { useSlots } from "../../../services/slots";
import { useSeasons } from "../../../services/settings";
import Season from "./Season";
import GenerateSlotDialog from "./GenerateSlotDialog";
import SendSlotRequestDialog from "./SendSlotRequestDialog";
import SystemContext from "../../../SystemContext";

const dateFormat = "DD/MM/YYYY";
const internalDateFormat = "YYYY-MM-DD";

const FlightsMissingSlots = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const [state] = useState({
    canEdit: props.canEdit,
  });
  const [generateSlot, setGenerateSlot] = useState({
    open: false,
    record: {}
  });
  const [sendSlot, setSendSlot] = useState({
    open: false,
    record: {}
  });

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required)
    extraKeys.push(required);

  const slots = useSlots({tableRef, queryClient, extraKeys});
  const seasons = useSeasons({ });

  if (!slots.data) {
    SystemData.toggleLoadingProgress(true, "Searching flights that are missing a valid slot ...");
  } else {
    SystemData.toggleLoadingProgress(false, null);
  }

  const refreshList = async () => {
    queryClient.invalidateQueries(["Slots", 0, 0, true]);
  }

  const replaceAt = (string, index, replacement) => {
      return string.substr(0, index) + replacement + string.substr(index + replacement.length);
  }

  const handleNewSlot = (slotRequirement) => {
    let season = __.find(seasons.data, {seasonCode: slotRequirement.seasonCode});
    if (season === undefined)
      season = getSeason(seasons.data, moment().format(internalDateFormat));

    const slotStartDate = moment(slotRequirement.periodFrom, internalDateFormat);
    const slotEndDate = moment(slotRequirement.periodTo, internalDateFormat);
    const seasonStart = moment(season.seasonsStarts, internalDateFormat);
    const seasonEnd = moment(season.seasonsEnds, internalDateFormat);
    const earliestSlotRequest = seasonStart.isAfter(moment.utc().add(1,'day')) ? seasonStart : moment.utc().add(1,'day');

    const record =
    {
      newSlotMode: true,
      status: "REQUIRED",
      slotType: "TURNROUND",
      slotPeriodFrom:  slotStartDate.isSameOrAfter(earliestSlotRequest) ? slotStartDate.format(internalDateFormat) : earliestSlotRequest.format(internalDateFormat),
      slotPeriodTo: slotEndDate.isAfter(seasonEnd) ? seasonEnd.format(internalDateFormat) : slotEndDate.format(internalDateFormat),
      airportCode: slotRequirement.airportCode,
      arrivalAirlineCode: slotRequirement.inboundAirlineCode,
      arrivalFlightNo: slotRequirement.inboundFlightNo,
      arrivalTime: slotRequirement.inboundFlightTime,
      departureAirlineCode: slotRequirement.outboundAirlineCode,
      departureFlightNo: slotRequirement.outboundFlightNo,
      departureTime: slotRequirement.flightTime,
      originAirportCode: slotRequirement.originAirportCode,
      destinationAirportCode: slotRequirement.destinationAirportCode,
      days: replaceAt(".......", slotRequirement.dow - 1, slotRequirement.dow.toString()),
      slotActionCode: "N",
      arrivalService: "H",
      departureService: "H",
      generalInfo: "REGARDS MAGMA AVIATION",
      season: season
    }

    setGenerateSlot({...generateSlot, open: true, newSlot: true, record});
  }

  const getColumns = (props) => {

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...(ColumnWidth(100, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "At Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <div>
              <span> {rowData.airportCode} </span>
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period From",
        field: "periodFrom",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.periodFrom).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = moment.utc(rowData.periodFrom).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period To",
        field: "periodTo",
        editable: "never",
        render: rowData => {
          const displayDate = moment.utc(rowData.periodTo).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130)),
        title: "Season",
        field: "seasonCode",
        editable: "never",
        render: rowData => <Season slotFrom={rowData.startDate} />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(65)),
        title: "Day",
        field: "dow",
        editable: "never",
      },
      {
        ...baseColumn,
        ...(ColumnWidth(90, 8)),
        title: "Inbound Flight",
        field: "inboundAirlineCode",
        editable: "never",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.inboundAirlineCode} ${rowData.inboundFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.inboundAirlineCode}-${rowData.inboundFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.inboundAirlineCode}${rowData.inboundFlightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        render: (props) => (
          <div>
            {props.inboundAirlineCode}&nbsp;{props.inboundFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120, 8)),
        title: "Origin Airport",
        field: "originAirportCode",
        editable: "never",
        sorting: true,
        render: (props) => (
          <div>
            {props.originAirportCode}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, [2,7])),
        title: "Arrival Time",
        field: "inboundFlightTime",
        render: rowData => (
          <div>{rowData.inboundFlightTime?.substr(0,Math.min(rowData.inboundFlightTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Departure Time",
        field: "flightTime",
        render: rowData => (
          <div>{rowData.flightTime?.substr(0,Math.min(rowData.flightTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120, 8)),
        title: "Destination Airport",
        field: "destinationAirportCode",
        editable: "never",
        sorting: true,
        render: (props) => (
          <div>
            {props.destinationAirportCode}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(90, 8)),
        title: "Outbound Flight",
        field: "outboundAirlineCode",
        editable: "never",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.outboundAirlineCode} ${rowData.outboundFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.outboundAirlineCode}-${rowData.outboundFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.outboundAirlineCode}${rowData.outboundFlightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        render: (props) => (
          <div>
            {props.outboundAirlineCode}&nbsp;{props.outboundFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        title: "Slot Level",
        field: "slotLevel",
        editable: "never",
      },
    ];
  };


  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //overflowY: "scroll",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
          Action: _props => {
            let props = {..._props};
            if (typeof props.action.action === "function") {
              props.action = {..._props.action, ..._props.action.action(_props.data)};
            }

            if (props.action.icon==="generate") {
              return (
                <Tooltip title={props.action.tooltip}>
                  <Button
                    style={{height: 24, marginTop: 6}}
                    variant="contained"
                    color="primary"
                    startIcon={<PlayIcon/>}
                    className={classes.button}
                    onClick={() => {
                      props.action.onClick(props.data)
                    }}
                  >
                    {props.action.text}
                  </Button>
                </Tooltip>
              )
            } else if (props.action.icon) {
              if (props.action.tooltip) {
                return (<Tooltip title={props.action.tooltip}><IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton></Tooltip>)
              }
              return (<IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton>)
            } else {
              return (<></>)
            }
          }
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          (rowData) => ({
            icon: "generate",
            text: "Request",
            tooltip: "Create slot request for flight",
            onClick: handleNewSlot,
            disabled: !state.canEdit
          }),
        ]}
        title="Flights Missing Slots"
        columns={getColumns({canEdit: state.canEdit})}
        data={slots.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return false;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
        }}
      />

      {generateSlot.open ?
        <GenerateSlotDialog
          record={generateSlot.record}
          canEdit={state.canEdit}
          close={()=>{setGenerateSlot({open: false, record: {}})}}
          send={(slotRequest, slotRecord, originalRecord)=>{
            setSendSlot({open: true, request: slotRequest, record: slotRecord, originalRecord: originalRecord});
            setGenerateSlot({open: false, record: {}});
          }}
          classes={classes}
          dialogStates={{showDialog: true}}
          newSlot={generateSlot.newSlot}
          seasons={seasons.data}
        />
        : <></>
      }
      {sendSlot.open ?
        <SendSlotRequestDialog
          request={sendSlot.request}
          record={sendSlot.record}
          originalRecord={sendSlot.originalRecord}
          canEdit={state.canEdit}
          close={()=>{setSendSlot({open: false, record: {}})}}
          classes={classes}
          dialogStates={{showDialog: true, airport: sendSlot.record?.airport}}
        />
        : <></>
      }

    </div>
  );

}

export default FlightsMissingSlots;
